import { COUNTRIES } from "@/i18n/countries";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

/**
 * Reusable multi-country checkbox row for admin edit forms.
 *
 * Props:
 *   value:    string[]  — currently-selected ISO codes (e.g. ["HK","AU"])
 *   onChange: (next: string[]) => void
 *   label:    string    — heading text
 *   hint:     string    — muted helper text below the heading
 *   testid:   string    — data-testid prefix for row + each checkbox
 *
 * Empty selection = "available in every market" (matches the backend
 * `_matches_country` semantics — a row with no country_codes is treated
 * as global). We surface that as a warning banner so admins know they're
 * about to publish globally.
 */
export default function CountryCodesPicker({
    value = [],
    onChange,
    label = "Available in",
    hint = "Tick the markets where this row should appear. Leaving everything unticked means available in every market.",
    testid = "country-codes",
}) {
    const toggle = (code) => {
        const next = value.includes(code)
            ? value.filter((c) => c !== code)
            : [...value, code];
        onChange(next);
    };

    const selectAll = () => onChange(COUNTRIES.map((c) => c.code));
    const clear = () => onChange([]);
    const allTicked = value.length === COUNTRIES.length;

    return (
        <div className="rounded-xl border border-navy-100 bg-cream/40 p-4" data-testid={testid}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <Label className="text-navy-700 text-xs uppercase tracking-widest">
                        {label}
                    </Label>
                    <p className="text-[11px] text-navy-500 mt-1 max-w-md leading-snug">
                        {hint}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        type="button"
                        onClick={allTicked ? clear : selectAll}
                        data-testid={`${testid}-toggle-all`}
                        className="text-[11px] uppercase tracking-widest text-gold-600 hover:text-gold-700 underline underline-offset-4"
                    >
                        {allTicked ? "Clear all" : "Select all"}
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mt-3">
                {COUNTRIES.map((c) => {
                    const on = value.includes(c.code);
                    return (
                        <label
                            key={c.code}
                            htmlFor={`${testid}-${c.code}`}
                            className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer transition-colors ${
                                on ? "bg-white shadow-soft" : "hover:bg-white/60"
                            }`}
                        >
                            <Checkbox
                                id={`${testid}-${c.code}`}
                                data-testid={`${testid}-${c.code}`}
                                checked={on}
                                onCheckedChange={() => toggle(c.code)}
                                className="data-[state=checked]:bg-navy-700 data-[state=checked]:border-navy-700"
                            />
                            <span className="text-base leading-none">{c.flag}</span>
                            <span className="text-sm text-navy-700">{c.name}</span>
                        </label>
                    );
                })}
            </div>
            {value.length === 0 && (
                <div
                    data-testid={`${testid}-empty-warning`}
                    className="text-[11px] text-gold-700 bg-gold-50 border border-gold-200 rounded-md px-2 py-1 mt-3"
                >
                    ⚠️ No markets selected — this row will appear in every country.
                </div>
            )}
        </div>
    );
}
